import { useEffect, useState } from "react";
import { listKudosTypes, sendRecognition, listRecognitions, fetchRecognitionLeaderboard } from "../lib/api";
import { fetchEmployees } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Award, Send, Trophy, Loader2, Sparkles } from "lucide-react";

export default function RecognitionModule({ currentEmployeeId, mode = "employee" }) {
  const [kudosTypes, setKudosTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [feed, setFeed] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tab, setTab] = useState("feed");
  const [sendOpen, setSendOpen] = useState(false);
  const [form, setForm] = useState({ to_employee_id: "", kudos_type: "", message: "", visibility: "public" });

  const reload = () => {
    if (mode === "employee") {
      listRecognitions({ employee_id: currentEmployeeId, direction: "received" }).then((d) => setFeed(d.recognitions || []));
    } else {
      listRecognitions({}).then((d) => setFeed(d.recognitions || []));
    }
    fetchRecognitionLeaderboard().then((d) => setLeaderboard(d.leaderboard || []));
  };

  useEffect(() => {
    listKudosTypes().then((d) => setKudosTypes(d.types || []));
    fetchEmployees().then((d) => setEmployees(Array.isArray(d) ? d : (d?.employees || [])));
    reload();
  }, [currentEmployeeId, mode]);

  const send = async () => {
    if (!form.to_employee_id || !form.kudos_type || !form.message.trim()) { toast.error("Alıcı, kudos ve mesaj zorunlu"); return; }
    try {
      await sendRecognition({ ...form, from_employee_id: currentEmployeeId, reward_points: 10 });
      toast.success("Takdirin gönderildi 🎉");
      setSendOpen(false);
      setForm({ to_employee_id: "", kudos_type: "", message: "", visibility: "public" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gönderim başarısız");
    }
  };

  return (
    <div className="space-y-4" data-testid="recognition-module">
      <div className="card-surface p-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Award className="w-3.5 h-3.5 text-amber-600" /> Takdir & Ödül (YTN — Recognition)</div>
          <div className="text-sm text-slate-600 mt-1">Takım arkadaşlarına kudos kartı gönder; aylık liderlik tablosuna yansır.</div>
        </div>
        {currentEmployeeId && (
          <Button size="sm" onClick={() => setSendOpen(true)} data-testid="send-recognition-btn"><Send className="w-3.5 h-3.5 mr-1" /> Takdir Gönder</Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        <button onClick={() => setTab("feed")} className={`px-3 py-2 text-sm border-b-2 flex items-center gap-1.5 ${tab === "feed" ? "border-indigo-600 text-indigo-700 font-medium" : "border-transparent text-slate-500"}`} data-testid="tab-recognition-feed">
          <Sparkles className="w-3.5 h-3.5" /> Akış
        </button>
        <button onClick={() => setTab("leaderboard")} className={`px-3 py-2 text-sm border-b-2 flex items-center gap-1.5 ${tab === "leaderboard" ? "border-indigo-600 text-indigo-700 font-medium" : "border-transparent text-slate-500"}`} data-testid="tab-recognition-leaderboard">
          <Trophy className="w-3.5 h-3.5" /> Liderlik Tablosu
        </button>
      </div>

      {tab === "feed" && (
        <div className="space-y-2">
          {feed.length === 0 ? <div className="text-sm text-slate-500 italic p-6 text-center">Henüz takdir yok.</div> : feed.map((r) => (
            <div key={r.id} className="card-surface p-3 flex items-start gap-3" data-testid={`recognition-${r.id}`}>
              <img src={r.from_avatar} alt={r.from_employee_name} className="w-10 h-10 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <b>{r.from_employee_name}</b> → <b>{r.to_employee_name}</b>
                  <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100">{r.kudos_label}</span>
                </div>
                <div className="text-sm text-slate-700 mt-1 italic">&quot;{r.message}&quot;</div>
                <div className="text-[10px] text-slate-400 mt-1">{new Date(r.created_at).toLocaleString("tr-TR")} · +{r.reward_points} puan</div>
              </div>
              <img src={r.to_avatar} alt={r.to_employee_name} className="w-8 h-8 rounded-full object-cover opacity-80" />
            </div>
          ))}
        </div>
      )}

      {tab === "leaderboard" && (
        <div className="card-surface p-4">
          {leaderboard.length === 0 ? <div className="text-sm text-slate-500 italic text-center py-6">Henüz veri yok.</div> : (
            <div className="space-y-2">
              {leaderboard.map((l, i) => (
                <div key={l.employee_id} className="flex items-center gap-3 p-2 rounded-lg bg-white border border-slate-100" data-testid={`leaderboard-${l.employee_id}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${i === 0 ? "bg-amber-500" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-orange-600" : "bg-slate-300"}`}>
                    {i + 1}
                  </div>
                  <img src={l.avatar} alt={l.name} className="w-9 h-9 rounded-full object-cover" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{l.name}</div>
                    <div className="text-[10px] text-slate-500">{l.department} · {l.recognition_count} takdir</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-amber-600">{l.total_points}</div>
                    <div className="text-[9px] text-slate-500 uppercase">puan</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Send dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="send-recognition-dialog">
          <DialogHeader><DialogTitle>Takdir Gönder</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600">Kime</label>
              <Select value={form.to_employee_id} onValueChange={(v) => setForm({ ...form, to_employee_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Alıcı..." /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {employees.filter((e) => e.id !== currentEmployeeId).slice(0, 100).map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} · {e.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Kudos Türü</label>
              <Select value={form.kudos_type} onValueChange={(v) => setForm({ ...form, kudos_type: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Kudos seç..." /></SelectTrigger>
                <SelectContent className="bg-white">
                  {kudosTypes.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Mesaj</label>
              <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Somut bir örnek yaz: 'Q3 sunumunda proje takımını...'" data-testid="recognition-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>İptal</Button>
            <Button onClick={send} data-testid="recognition-send-submit">Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
